import { useCallback, type ReactNode } from 'react'
import { useReducedMotion } from 'framer-motion'
import type {
  CompiledKMachine,
  KExecutionState,
  KLayout,
  TMState,
  TuringMachine,
} from '../engine/types'
import { readTape } from '../engine/turing'
import { useKMachineRunner, useTuringMachineRunner } from '../hooks/useMachineRunner'
import { decodeTreeResult } from '../programs/tree-build-ku'
import { Controls } from './Controls'
import { GraphRenderer } from './GraphRenderer'
import { MachinePanel } from './MachinePanel'
import { RuleDisplay } from './RuleDisplay'
import { TapeRenderer } from './TapeRenderer'

interface ComparisonFigureProps {
  kuMachine: CompiledKMachine
  kuInitial: KExecutionState
  kuLayout: (state: KExecutionState) => KLayout
  tmMachine: TuringMachine
  tmInitial: TMState
  caption: ReactNode
  showComparison?: boolean
}

function narrateK(state: KExecutionState): string {
  if (!state.lastTrace) {
    return 'T0 joins the tree root to the first address cell. Its radius-2 active part contains exactly the local structure eligible for replacement.'
  }
  if (state.outcome === 'no-result') {
    return 'No rule input has this rooted typed form. The transition is undefined, so the computation stops without a result.'
  }
  const applied = state.lastTrace.events.find((event) => event.type === 'rule-applied')
  if (state.outcome === 'result') {
    return 'The finalization pair creates T1 beside the selected leaf. Only T1’s connected component counts as the result; detached address bits are ignored.'
  }
  const direction = applied?.type === 'rule-applied'
    ? applied.tags.includes('left') ? 'left' : applied.tags.includes('right') ? 'right' : null
    : null
  return direction
    ? `The ${direction} routing pair preserved frontier L, replaced the interior of U, and left the consumed bit as a disconnected external vertex.`
    : `Applied ${applied?.type === 'rule-applied' ? applied.title : 'one neighborhood replacement'}.`
}

function narrateTM(state: TMState, blank: string): string {
  const symbol = readTape(state, blank)
  if (state.step === 0) {
    return 'The same tree is serialized as @*ab*cd# followed by the address. The head begins at the left edge and must scan to recover structural access.'
  }
  if (state.outcome === 'result') {
    return `The head has reached leaf ${symbol}. Its transition count is native to this particular tape encoding and program.`
  }
  if (state.outcome === 'no-result') {
    return `No transition is defined in state ${state.state} while reading ${symbol}; this computation has no result.`
  }
  if (state.state === 'read-addr') return 'Scan right to find the next unconsumed L/R address symbol.'
  if (state.state === 'seek-l' || state.state === 'seek-r') {
    return 'Scan left to recover the current root marker before following the selected branch.'
  }
  if (state.state.startsWith('skip-')) return 'Skip the serialized left subexpression to reach the right branch.'
  return 'Update the root marker, then resume the address scan.'
}

export function ComparisonFigure({
  kuMachine,
  kuInitial,
  kuLayout,
  tmMachine,
  tmInitial,
  caption,
  showComparison = false,
}: ComparisonFigureProps) {
  const reducedMotion = useReducedMotion() ?? false
  const ku = useKMachineRunner(kuInitial, kuMachine)
  const tm = useTuringMachineRunner(tmInitial, tmMachine)

  const advanceToMilestone = useCallback(() => {
    ku.pause()
    tm.pause()
    const kuMilestone = ku.current.milestones
    const tmMilestone = tm.current.milestones
    ku.advanceUntil(
      (state) => state.milestones > kuMilestone || state.outcome !== 'running',
      16,
    )
    tm.advanceUntil(
      (state) => state.milestones > tmMilestone || state.outcome !== 'running',
      240,
    )
  }, [ku, tm])

  const sharedFinished = ku.current.outcome !== 'running' && tm.current.outcome !== 'running'
  const traversalComplete = ku.current.milestones >= 2 && tm.current.milestones >= 2
  const kuResult = decodeTreeResult(ku.current)
  const tmSymbol = readTape(tm.current, tmMachine.blank)
  const tmResult = ['a', 'b', 'c', 'd'].includes(tmSymbol) && tm.current.outcome === 'result'
    ? tmSymbol
    : null

  return (
    <figure className={`km-explorable${showComparison ? ' is-comparing' : ''}`}>
      {showComparison ? <div className="km-linked-control">
        <div>
          <span className="km-linked-eyebrow">Linked by meaning, not by step</span>
          <strong>
            Milestone {Math.min(ku.current.milestones, tm.current.milestones)} of 2
          </strong>
          <p>Advance each implementation until it consumes the next address symbol.</p>
        </div>
        <button
          type="button"
          className="km-button km-button-milestone"
          onClick={advanceToMilestone}
          disabled={sharedFinished}
        >
          {sharedFinished
            ? 'Both computations finished'
            : traversalComplete
              ? 'Finish both computations'
              : 'Advance both to next milestone'}
        </button>
      </div> : null}

      <div className={`km-comparison-grid${showComparison ? '' : ' is-single'}`}>
        <MachinePanel
          eyebrow="1958 neighborhood replacement"
          title="K machine"
          outcome={ku.current.outcome}
          output={kuResult ? `leaf ${kuResult}` : undefined}
          narration={narrateK(ku.current)}
          renderer={(
            <GraphRenderer
              state={ku.current}
              machine={kuMachine}
              layout={kuLayout(ku.current)}
              reducedMotion={reducedMotion}
            />
          )}
          controls={(
            <Controls
              playing={ku.playing}
              onPlay={ku.play}
              onPause={ku.pause}
              onStep={ku.step}
              onBack={() => ku.goToStep(Math.max(0, ku.index - 1))}
              onReset={ku.reset}
              speed={ku.speed}
              onSpeedChange={ku.setSpeed}
              stepCount={ku.current.step}
              canBack={ku.index > 0}
              countLabel="Replacements"
              outcome={ku.current.outcome}
            />
          )}
          details={<RuleDisplay type="ku" machine={kuMachine} state={ku.current} />}
        />

        {showComparison ? <MachinePanel
          eyebrow="Fixed tape serialization"
          title="Turing machine"
          outcome={tm.current.outcome}
          output={tmResult ? `leaf ${tmResult}` : undefined}
          narration={narrateTM(tm.current, tmMachine.blank)}
          renderer={(
            <TapeRenderer
              state={tm.current}
              blank={tmMachine.blank}
              reducedMotion={reducedMotion}
            />
          )}
          controls={(
            <Controls
              playing={tm.playing}
              onPlay={tm.play}
              onPause={tm.pause}
              onStep={tm.step}
              onBack={() => tm.goToStep(Math.max(0, tm.index - 1))}
              onReset={tm.reset}
              speed={tm.speed}
              onSpeedChange={tm.setSpeed}
              stepCount={tm.current.step}
              canBack={tm.index > 0}
              countLabel="Head transitions"
              outcome={tm.current.outcome}
            />
          )}
          details={<RuleDisplay type="tm" machine={tmMachine} state={tm.current} />}
        /> : null}
      </div>

      {showComparison ? <div className="km-comparison-limit">
        <strong>Comparison limit.</strong> These counters are native to two chosen implementations.
        They are not a common complexity measure, and the tape program shown here is restricted to
        this fixed depth-two tree.
      </div> : null}
      <figcaption>{caption}</figcaption>
    </figure>
  )
}
